/**
 * CUSTOM 2026-07-28 — keeps variant-scoped sections in sync with the Format picker.
 *
 * THE BUG THIS FIXES
 * On the Set template, `Format` (Tablets / Gummies) is a real variant, and the
 * supplement half of the box genuinely changes with it — different actives, a
 * different ingredient declaration, a different serving. Those render from
 * variant-scoped `hl_` metafields, read server-side via
 * `product.selected_or_first_available_variant`.
 *
 * Horizon's variant picker switches variants client-side. Its `variantChanged`
 * handler passes `morphElementSelector: undefined` for an ordinary same-product
 * switch, which morphs only the picker's own section — the buy box. Sections
 * further down the page are never touched, so "What's inside", the ingredient
 * declaration and the specs stayed on whichever format the page loaded with.
 * Switching to Gummies left a Tablets page behind.
 *
 * WHY THIS IS CHEAP
 * The picker already fetches the FULL page HTML for the newly selected variant
 * (`buildRequestUrl` appends `option_values=`, with no `section_id`, so the
 * response is the whole document). It just discards everything outside the buy
 * box. The parsed document is handed to listeners as `event.promise` →
 * `detail.html`, so this adds ZERO extra network requests — it reads the
 * response Horizon already had.
 *
 * WHY A DOCUMENT-LEVEL LISTENER IS SAFE
 * `shopify:product:select` is a Shopify standard storefront event, and those
 * are specified to "dispatch from the most specific available contextual
 * element and bubble to document" — so listening at document level is the
 * documented contract, not a bubbling accident.
 *
 * WHY IT DOESN'T EDIT ANY HORIZON FILE
 * The alternative was setting `morphElementSelector: 'main'` inside
 * `assets/variant-picker.js`, which would re-morph the entire page for every
 * variant switch on every product in the store. This is additive instead:
 * a net-new asset, loaded by the two custom sections that need it.
 *
 * Only innerHTML is replaced, never the section element itself — the tab
 * behaviour in `sections/hl-ingredient-transparency.liquid` is bound by
 * delegation on that element and must survive the swap.
 *
 * UPSTREAM risk: LOW-MEDIUM. Depends on the documented standard-event payload
 * (`event.promise` resolving with `detail.html`), not on Horizon internals.
 * If Horizon ever starts morphing `main` on variant change this becomes
 * redundant but stays harmless — it would just re-write identical markup.
 */

/** Sections that render variant-scoped content and must follow the picker. */
const VARIANT_SCOPED_SECTIONS = ['.hl-ingtrans', '.hl-setcontents'];

/** Guard against double-binding: both sections load this module by the same URL. */
if (!window.__hlVariantSectionsBound) {
  window.__hlVariantSectionsBound = true;

  document.addEventListener('shopify:product:select', (event) => {
    const target = event.target;

    // Product cards and quick-add dialogs render their own mini variant pickers.
    // Those must never repaint the main product page's sections.
    if (target instanceof Element && (target.closest('product-card') || target.closest('quick-add-dialog'))) {
      return;
    }

    const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    if (!promise) return;

    promise
      .then((resolved) => {
        const html = resolved?.detail?.html;
        if (!html) return;

        for (const selector of VARIANT_SCOPED_SECTIONS) {
          for (const current of document.querySelectorAll(selector)) {
            if (!current.id) continue;

            const incoming = html.getElementById(current.id);
            if (!incoming) continue;

            // The tablist resets to its first tab on re-render, which is jarring
            // if the shopper was reading "Free from" and only changed format.
            // Capture the index first, restore it after.
            const tabs = Array.from(current.querySelectorAll('.hl-ingtrans__tab'));
            const activeTabIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));

            current.innerHTML = incoming.innerHTML;

            if (activeTabIndex > 0) {
              const newTabs = current.querySelectorAll('.hl-ingtrans__tab');
              // Tab count can differ between variants — a format whose data is
              // blank renders fewer tabs — so only restore if it still exists.
              newTabs[activeTabIndex]?.dispatchEvent(new Event('click', { bubbles: true }));
            }
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[hl-variant-sections] product select promise rejected:', error);
        }
      });
  });
}
